import * as z from 'zod';

/**
 * The order-creation contract, verified live against `POST /orders/eval` on
 * `tback.noor.uz` (which accepts the same body as `POST /orders` but only
 * prices it).
 *
 * An order is a list of DESTINATIONS — the first is the pickup (`origin`), the
 * rest are drop-offs — plus a `delivery` block describing the vehicle and
 * options. Multi-leg orders are the normal case, not an edge case: the old
 * screen lets a merchant add both extra starting points and extra destinations.
 */

/** A map coordinate. Sent as numbers even though the old form held strings. */
export const legacyPointSchema = z.object({
  long: z.number(),
  lat: z.number(),
});

/** Who to contact at a stop. Only `phone` is meaningful for a drop-off. */
export const legacyDraftClientSchema = z.object({
  phone: z.string().default(''),
  name: z.string().default(''),
  email: z.string().default(''),
});

/**
 * One line item of cargo as the WIRE takes it.
 *
 * The optional numeric fields mirror the old panel's two payload builders: the
 * CREATE screen sends `Number(x)` (so a blank arrives as `0`), while the EDIT
 * screen sends `undefined` for a blank (`edit/index.tsx:457-471`) — a PATCH
 * carries the full body, and writing `0` where the merchant left a field empty
 * would overwrite stored dimensions with zeros.
 *
 * `photo` is preserved because the edit PATCH is full-body: an omission is a
 * deletion, and the old panel carries it through (`edit/index.tsx:472`).
 */
export const legacyProductItemSchema = z.object({
  name: z.string().default(''),
  price_per_unit: z.number().default(0),
  quantity: z.number().optional(),
  weight: z.number().optional(),
  height: z.number().optional(),
  width: z.number().optional(),
  length: z.number().optional(),
  photo: z.string().optional(),
});

/**
 * One line item as the FORM holds it: the wire fields plus bookkeeping the
 * screen needs and the wire never sees —
 *
 * - `id`: a stable identity so the drop-off assignment picker can address an
 *   item across re-renders (the old form's `uuidv4()` per item).
 * - `destinationIndex`: the id of the DROP-OFF stop this item is assigned to
 *   (the old form's name for it, kept for traceability). An unassigned item
 *   blocks submission — the courier cannot deliver goods with no address.
 *
 * Numeric fields are concrete numbers with `0` meaning "blank": the inputs
 * render `0` as an empty box, and `buildDraft` converts blanks per mode.
 */
export const legacyDraftItemSchema = z.object({
  id: z.string(),
  name: z.string().default(''),
  price_per_unit: z.number().default(0),
  quantity: z.number().default(0),
  weight: z.number().default(0),
  height: z.number().default(0),
  width: z.number().default(0),
  length: z.number().default(0),
  photo: z.string().optional(),
  destinationIndex: z.string().optional(),
});

export const legacyDraftProductsSchema = z.object({
  /** Cargo category — see `order_documents` in the old app (0 = other). */
  type_id: z.number().default(0),
  description: z.string().default(''),
  items: z.array(legacyDraftItemSchema).default([]),
});

/**
 * One stop as the FORM holds it. `type` is `origin` for pickups and
 * `destination` for drop-offs; `order` is the sequence index the courier
 * follows.
 *
 * `floor` is a string here because it is a text input. On the wire it must be
 * a NUMBER or null — see `legacyWireDestinationSchema`.
 */
export const legacyDestinationSchema = z.object({
  /**
   * The stop's identity in the FORM — what an item's `destinationIndex` points
   * at. A fresh stop gets a generated id; a rehydrated stop reuses the server
   * leg id so edit-mode assignment matching survives a round trip.
   */
  id: z.string(),
  /**
   * The server leg id, present only on stops rehydrated from a placed order.
   * The edit PATCH must echo it so the backend amends the existing leg rather
   * than replacing it (`edit/index.tsx:476,531`); new stops omit it.
   */
  serverId: z.string().optional(),
  /**
   * When the courier has already ARRIVED at this leg the old edit screen locks
   * every one of its fields and hides its remove button — the stop has already
   * happened (`ResDestination.tsx:93`, `DestinationBox.tsx:168-194`).
   */
  arrivedAt: z.string().nullable().default(null),
  location: legacyPointSchema,
  order: z.number().default(0),
  address: z.string().default(''),
  entrance: z.string().default(''),
  door_phone: z.string().default(''),
  floor: z.string().default(''),
  apartment: z.string().default(''),
  comment: z.string().default(''),
  client: legacyDraftClientSchema,
  products: legacyDraftProductsSchema,
  type: z.enum(['origin', 'destination']),
});

/** The goods block as the WIRE takes it (items without form bookkeeping). */
export const legacyWireProductsSchema = z.object({
  type_id: z.number().default(0),
  description: z.string().default(''),
  items: z.array(legacyProductItemSchema).default([]),
});

/**
 * One stop as the WIRE takes it. Differences from the form shape:
 *
 * - `floor` — the backend rejects a string: sending `floor: ""` answers
 *   `400 bad.request` and the whole price preview fails, which surfaced as a
 *   permanently blank total. Verified live against `POST /orders/eval` — a
 *   number or `null` is accepted, `""` is not. The old panel sends
 *   `Number(el.floor) || undefined`, which is the same rule.
 * - `id` — only sent on the EDIT path, echoing the server leg id (see
 *   `serverId` above); the create body carries none.
 * - `items` — wire items, stripped of `id`/`destinationIndex`.
 */
export const legacyWireDestinationSchema = z.object({
  id: z.string().optional(),
  location: legacyPointSchema,
  order: z.number().default(0),
  address: z.string().default(''),
  entrance: z.string().default(''),
  door_phone: z.string().default(''),
  floor: z.number().nullable().default(null),
  apartment: z.string().default(''),
  comment: z.string().default(''),
  client: legacyDraftClientSchema,
  products: legacyWireProductsSchema,
  type: z.enum(['origin', 'destination']),
});

export const legacyDeliveryBlockSchema = z.object({
  door_to_door: z.boolean().default(false),
  /** Which vehicle class — an `equipments` id. */
  equipment_id: z.number(),
  /** Always `EXPRESS` from this screen; the enum exists for scheduled types. */
  type: z.string().default('EXPRESS'),
  /** Send the customer a tracking link by SMS. */
  send_link: z.boolean().default(false),
  /** The merchant's goods are already paid for, so the courier collects nothing. */
  product_paid: z.boolean().default(false),
  /** ISO timestamp for a scheduled pickup; null means "as soon as possible". */
  time: z.string().nullable().default(null),
});

/**
 * The wire body for `POST /orders` and `POST /orders/eval`.
 *
 * Stops are sent as TWO arrays — `origin` (pickups) and `destination`
 * (drop-offs) — not one combined list. `order` is a 1-BASED index into the
 * merged sequence, so it is what ties the two arrays back together into the
 * route the courier drives; the arrays themselves carry no cross-ordering.
 *
 * The screen holds a single ordered list and splits it here (`buildDraft`),
 * because one list is what the form renders and what "add a stop" operates on.
 */
export const legacyOrderDraftSchema = z.object({
  /**
   * Sent verbatim by the old panel on every order. Not merchant-specific and
   * not surfaced anywhere in its UI — reproduced because the backend has only
   * ever been exercised with it present.
   */
  vendor_order_id: z.string().default('2'),
  is_business: z.boolean().default(true),
  origin: z.array(legacyWireDestinationSchema).min(1),
  destination: z.array(legacyWireDestinationSchema).min(1),
  /** `BALANCE` for corporate accounts, otherwise the merchant's choice. */
  payment_type: z.string().default('BALANCE'),
  delivery: legacyDeliveryBlockSchema,
});

export type LegacyPoint = z.infer<typeof legacyPointSchema>;
export type LegacyProductItem = z.infer<typeof legacyProductItemSchema>;
export type LegacyDraftItem = z.infer<typeof legacyDraftItemSchema>;
export type LegacyDestination = z.infer<typeof legacyDestinationSchema>;
export type LegacyWireDestination = z.infer<typeof legacyWireDestinationSchema>;
export type LegacyOrderDraft = z.infer<typeof legacyOrderDraftSchema>;

/**
 * The order body the CREATE-WEBVIEW screen sends (`create-webview/index.tsx:
 * 144-224`) — a narrower shape than the main draft, derived from the same
 * schemas rather than hand-modelled:
 *
 * - NO `is_business` at all (the embedding consumer apps are not businesses);
 * - origin entries carry NO `products` block;
 * - `delivery` has no `product_paid`;
 * - `order` is numbered per-array (`ind + 1` / `ind + 2`), not across the
 *   combined route — the old webview's own quirk, reproduced.
 */
export interface LegacyWebviewOrderDraft {
  vendor_order_id: string;
  origin: Omit<LegacyWireDestination, 'products' | 'type' | 'id'>[];
  destination: Omit<LegacyWireDestination, 'type' | 'id'>[];
  payment_type: string;
  delivery: Omit<z.infer<typeof legacyDeliveryBlockSchema>, 'product_paid'>;
}

/**
 * `GET /vendors/locations?is_business=true` — the merchant's SAVED pickup
 * addresses, offered above the geocoder suggestions on origin stops (old
 * `DestinationBox.tsx:235-311`). Picking one fills the whole stop: location,
 * address, apartment, floor, entrance, comment, and the contact pair.
 *
 * This is the same endpoint `entities/legacy-branch` reads, but FSD forbids
 * one entity importing another, so this slice carries its own thin schema with
 * exactly the fields the order form fills.
 */
export const legacySavedLocationSchema = z.object({
  id: z.number().optional(),
  location: legacyPointSchema,
  name: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  entrance: z.string().nullable().default(null),
  /** A string on read even though the order wire wants a number. */
  floor: z.union([z.string(), z.number()]).nullable().default(null),
  apartment: z.string().nullable().default(null),
  comment: z.string().nullable().default(null),
  client_name: z.string().nullable().default(null),
  client_phone: z.string().nullable().default(null),
  tag: z.string().nullable().default(null),
});

export const legacySavedLocationListSchema = z
  .array(legacySavedLocationSchema)
  .nullable()
  .default([]);

export type LegacySavedLocation = z.infer<typeof legacySavedLocationSchema>;

/**
 * `POST /orders/eval` — the price preview. Captured live; `evaluated_stage`
 * carries a stage number (28 = out of delivery zone) that the screen uses to
 * block submission with a meaningful message rather than letting the create
 * fail.
 */
export const legacyOrderEvalSchema = z.object({
  total_delivery_price: z.number().default(0),
  additional_fixed_price: z.number().default(0),
  /**
   * ETA at the pickup / drop-off, as ISO-8601 TIMESTAMP STRINGS
   * (`2026-08-13T11:17:22.022092Z`) — not durations.
   *
   * Typing these as numbers made the whole response fail to parse, so a
   * perfectly good 200 surfaced as a mutation error and the price silently
   * never appeared. Kept permissive (`string`, not `z.iso.datetime()`) because
   * the fractional-second precision varies and nothing here renders them yet.
   */
  eta_to_pu: z.string().nullable().default(null),
  eta_to_del: z.string().nullable().default(null),
  order_distance: z.number().default(0),
  dead_head_distance: z.number().default(0),
  evaluated_stage: z.number().default(0),
  /**
   * 1-BASED indices of the stops that fell outside the delivery zone,
   * returned alongside `evaluated_stage: 28`. The screen names them so the
   * merchant knows WHICH stop to move — the old panel silently relocated them
   * to a hardcoded park, which is worse: it changes the order under you.
   */
  out_of_zone_points: z.array(z.number()).nullable().default([]),
});

export type LegacyOrderEval = z.infer<typeof legacyOrderEvalSchema>;

/**
 * `GET /equipments` — the vehicle classes offered as "Tariffs" cards. Nested
 * under an `equipments` key rather than returned bare.
 */
export const legacyEquipmentSchema = z.object({
  id: z.number(),
  name: z.string().default(''),
  /** Storage-relative path; resolve through the file API. */
  photo: z.string().nullable().default(null),
  is_visible: z.boolean().default(true),
  /** Box dimensions in cm. */
  width: z.number().default(0),
  height: z.number().default(0),
  length: z.number().default(0),
  /** Grams — the card renders kg. */
  max_weight: z.number().default(0),
  /** Kilometres. */
  max_range: z.number().default(0),
  max_volume: z.number().default(0),
  max_order_count: z.number().default(0),
  linked_equipments: z.array(z.number()).nullable().default([]),
  load_capacity: z.number().default(0),
  category_id: z.number().nullable().default(null),
  equipment_type: z.number().default(0),
});

export const legacyEquipmentListSchema = z.object({
  equipments: z.array(legacyEquipmentSchema).nullable().default([]),
});

export type LegacyEquipment = z.infer<typeof legacyEquipmentSchema>;
